// The booking confirmation mail (DESK-OPERATOR-AND-MAIL-BRIEF §3, M2–M3).
// The desk renders the text (operator, 2026-10-27); the outbox stores it; the
// card link (D6) replaces CARD_LINK_MARK at send time. Pure.

import type { Instant, ReservationId } from './types.js';

/** Where the card link goes in a stored confirmation (the database's placeholder). */
export const CARD_LINK_MARK = '{{lienCarte}}';

export type ConfirmationMailStatus = 'awaiting_link' | 'queued' | 'sending' | 'sent' | 'failed';
export type ConfirmationMailLang = 'fr' | 'en' | 'es';

export interface ConfirmationMail {
  id: string;
  reservationId: ReservationId;
  propertyCode: string;
  to: string;
  lang: ConfirmationMailLang;
  subject: string;
  status: ConfirmationMailStatus;
  cardLink: string | null;
  attempts: number;
  error: string | null;
  preparedAt: Instant;
  sentAt: Instant | null;
}

export interface ConfirmationMailDraft {
  reservationId: ReservationId;
  /** The booking guest's own e-mail — the database refuses any other. */
  to: string;
  lang: ConfirmationMailLang;
  subject: string;
  /** Carries CARD_LINK_MARK once when the arrhes are paid by the secure card link; carries none for a
   *  bank-transfer confirmation (PB10), whose body holds the bank instructions instead. The database
   *  reads the mark: with it the mail waits for its link, without it the mail is ready. */
  body: string;
}

/** What a send answered: the row's new status, and why when it did not go. */
export interface ConfirmationMailSendResult {
  sent: boolean;
  status: ConfirmationMailStatus;
  reason: string | null;
}

export function confirmationDraftProblem(d: ConfirmationMailDraft): string | null {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.to.trim())) return 'L’e-mail du client manque ou est invalide.';
  if (!d.subject.trim()) return 'L’objet de la confirmation est vide.';
  // None is a bank-transfer confirmation (PB10); more than one would put the link in twice.
  if (d.body.split(CARD_LINK_MARK).length > 2) return 'Le texte de la confirmation a plusieurs emplacements pour le lien carte.';
  return null;
}

export const cardLinkProblem = (link: string): string | null =>
  /^https:\/\/\S+$/.test(link.trim()) && link.trim().length <= 2000
    ? null
    : 'Collez l’adresse https:// de la page sécurisée de la banque.';

/** French staff-facing status line. */
export function confirmationStatusFr(m: ConfirmationMail): string {
  switch (m.status) {
    case 'awaiting_link':
      return 'Confirmation à envoyer : lien carte à coller';
    case 'queued':
      return 'Prête à envoyer';
    case 'sending':
      return 'Envoi en cours…';
    case 'sent':
      return `Confirmation envoyée à ${m.to}${m.sentAt ? ` · ${new Date(m.sentAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}`;
    case 'failed':
      return `Échec de l’envoi${m.error ? ` : ${m.error}` : ''}`;
  }
}
